import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import './ScalableShieldSpinner.css';

const useStyles = makeStyles((theme) => ({
  spinnerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  spinnerContainer: {
    position: 'relative',
  },
}));

/**
 * ScalableShieldSpinner - A reusable, scalable loading spinner component
 * with an animated rotating gradient border and centered shield logo.
 *
 * @param {number}  size         - Size of the spinner in pixels (default: 350)
 * @param {string}  logoSrc      - Path to the logo image to display in center
 * @param {number}  borderWidth  - Width of the outer border (default: 10)
 * @param {string}  innerBgColor - Background color of inner circle.
 *                                 Defaults to '#E9f2f9' (light) or '#1c1c2e' (dark).
 * @param {number}  innerPadding - Padding inside the spinner (default: 10)
 * @param {boolean} darkMode     - When true, uses dark background and white-to-blue
 *                                 gradient ring suited for dark UI.
 */
const ScalableShieldSpinner = ({
  size = 350,
  logoSrc,
  borderWidth = 10,
  innerBgColor,
  innerPadding = 10,
  fullHeight = false,
  darkMode = false,
}) => {
  const classes = useStyles();
  const resolvedInnerBgColor = innerBgColor ?? (darkMode ? '#1c1c2e' : '#E9f2f9');

  const wrapperStyle = {
    ...(fullHeight && {
      height: '100vh',
      width: '100vw',
      background: resolvedInnerBgColor,
    }),
  };

  const loaderStyle = {
    width: `${size}px`,
    height: `${size}px`,
  };

  const afterStyle = {
    top: `${innerPadding}px`,
    left: `${innerPadding}px`,
    right: `${innerPadding}px`,
    bottom: `${innerPadding}px`,
    background: resolvedInnerBgColor,
    borderWidth: `${borderWidth}px`,
  };

  const logoContainerStyle = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `${size * 0.12}px`,
  };

  const logoStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  };

  return (
    <div className={classes.spinnerWrapper} style={wrapperStyle}>
      <div className={classes.spinnerContainer} style={loaderStyle}>
        <div className={`scalable-shield-loader${darkMode ? ' scalable-shield-loader--dark' : ''}`} style={loaderStyle}>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <div
            className="scalable-shield-loader-inner"
            style={afterStyle}
          ></div>
        </div>
        {logoSrc && (
          <div style={logoContainerStyle}>
            <img src={logoSrc} alt="Loading" style={logoStyle} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ScalableShieldSpinner;
